import { useEffect, useState } from "react";
import { adminApi, fmtDate, fmtDuration } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Mail } from "lucide-react";

export default function AdminCodeDetail({ code, adminKey, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await adminApi(adminKey).get(`/admin/codes/${code}/analytics`);
        setData(data);
      } catch (e) {
        toast.error("Failed to load analytics");
      } finally { setLoading(false); }
    };
    load();
  }, [code, adminKey]);

  if (loading) return <div className="p-10 text-muted-foreground">Loading…</div>;
  if (!data) return <div className="p-10 text-muted-foreground">No data</div>;

  return (
    <div className="px-6 md:px-10 py-10 max-w-7xl mx-auto">
      <button onClick={onBack} className="flex items-center gap-2 text-xs uppercase tracking-brand hover:text-primary mb-6">
        <ArrowLeft className="w-3.5 h-3.5" /> All codes
      </button>

      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="overline mb-2">{data.code.code} · {data.code.active ? "Active" : "Paused"}</p>
          <h1 className="font-display text-5xl tracking-tighter">{data.code.partner_name}</h1>
          {data.code.notes && <p className="mt-2 text-muted-foreground">{data.code.notes}</p>}
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-foreground/20 border border-foreground/20 mb-6">
        <div className="bg-background p-6">
          <p className="overline mb-2">Sessions</p>
          <p className="font-mono-data text-4xl tabular">{data.totals.sessions}</p>
        </div>
        <div className="bg-background p-6">
          <p className="overline mb-2">Slide views</p>
          <p className="font-mono-data text-4xl tabular">{data.totals.views}</p>
        </div>
        <div className="bg-background p-6">
          <p className="overline mb-2">Total time on deck</p>
          <p className="font-mono-data text-4xl tabular">{fmtDuration(data.totals.time_on_deck_ms || 0)}</p>
        </div>
        <div className="bg-background p-6">
          <p className="overline mb-2">Leads</p>
          <p className="font-mono-data text-4xl tabular text-primary">{data.totals.leads}</p>
        </div>
      </div>

      {/* Most engaged callout */}
      {data.most_engaged_slide && (
        <div className="bg-foreground text-background p-6 mb-10" data-testid="most-engaged-callout">
          <p className="overline text-background/60 mb-2">Most engaged slide</p>
          <div className="flex flex-col md:flex-row md:items-baseline md:justify-between gap-2">
            <p className="font-display text-3xl md:text-4xl">
              {String(data.most_engaged_slide.slide_idx + 1).padStart(2, "0")} · {data.most_engaged_slide.slide_label}
            </p>
            <p className="font-mono-data text-xl md:text-2xl text-primary">
              {fmtDuration(data.most_engaged_slide.total_ms)} · {data.most_engaged_slide.views} views
            </p>
          </div>
        </div>
      )}

      {/* Heatmap */}
      <div className="bg-white border border-foreground p-6 mb-10">
        <div className="flex items-baseline justify-between mb-6">
          <p className="overline">Slide engagement heatmap</p>
          <p className="text-[11px] uppercase tracking-brand text-muted-foreground">
            Bar = time spent · bold number = views
          </p>
        </div>
        {data.heatmap.length === 0 ? (
          <p className="text-muted-foreground text-sm">No views recorded yet.</p>
        ) : (
          <div className="space-y-2">
            {(() => {
              const maxMs = Math.max(1, ...data.heatmap.map((h) => h.total_ms || 0));
              return data.heatmap.map((s) => {
                const isTop = data.most_engaged_slide && s.slide_idx === data.most_engaged_slide.slide_idx;
                return (
                  <div key={s.slide_idx} className="grid grid-cols-12 gap-3 items-center">
                    <span className="col-span-1 font-mono-data text-xs text-muted-foreground">
                      {String(s.slide_idx + 1).padStart(2, "0")}
                    </span>
                    <span className={`col-span-3 font-display text-base ${isTop ? "text-primary" : ""}`}>
                      {s.slide_label || `Slide ${s.slide_idx + 1}`}
                    </span>
                    <div className="col-span-5 h-5 bg-muted relative">
                      <div
                        className={`absolute inset-y-0 left-0 ${isTop ? "bg-primary" : "bg-foreground"}`}
                        style={{ width: `${((s.total_ms || 0) / maxMs) * 100}%` }}
                      />
                    </div>
                    <span className="col-span-1 font-mono-data text-sm text-right font-semibold">{s.views}</span>
                    <span className="col-span-2 font-mono-data text-xs text-muted-foreground text-right">
                      {fmtDuration(s.total_ms)}
                    </span>
                  </div>
                );
              });
            })()}
          </div>
        )}
      </div>

      {/* Sessions */}
      <div className="bg-white border border-foreground mb-10">
        <div className="px-6 py-4 border-b border-border">
          <p className="overline">Recent sessions</p>
        </div>
        {data.sessions.length === 0 ? (
          <p className="p-6 text-muted-foreground text-sm">No sessions yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left">
                <tr>
                  <th className="px-5 py-3 overline">Started</th>
                  <th className="px-5 py-3 overline">Last seen</th>
                  <th className="px-5 py-3 overline">Location</th>
                  <th className="px-5 py-3 overline text-right">Slides</th>
                  <th className="px-5 py-3 overline text-right">Time on deck</th>
                  <th className="px-5 py-3 overline">User agent</th>
                </tr>
              </thead>
              <tbody>
                {data.sessions.map((s) => (
                  <tr key={s.session_id} className="border-t border-border" data-testid={`session-row-${s.session_id.slice(0, 8)}`}>
                    <td className="px-5 py-3 font-mono-data text-xs">{fmtDate(s.started_at)}</td>
                    <td className="px-5 py-3 font-mono-data text-xs">{fmtDate(s.last_seen_at)}</td>
                    <td className="px-5 py-3 text-xs">
                      <div className="font-display text-sm leading-tight">{s.location_label || "—"}</div>
                      {s.ip_address && (
                        <div className="text-[10px] text-muted-foreground font-mono-data mt-0.5">{s.ip_address}</div>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right font-mono-data">{s.slide_count || 0}</td>
                    <td className="px-5 py-3 text-right font-mono-data">{fmtDuration(s.time_on_deck_ms)}</td>
                    <td className="px-5 py-3 text-xs text-muted-foreground truncate max-w-md">
                      {s.user_agent || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Leads from this partner */}
      <div className="bg-white border border-foreground">
        <div className="px-6 py-4 border-b border-border">
          <p className="overline">Leads from {data.code.partner_name}</p>
        </div>
        {data.leads.length === 0 ? (
          <p className="p-6 text-muted-foreground text-sm">No leads yet.</p>
        ) : (
          <ul className="divide-y divide-border">
            {data.leads.map((l) => (
              <li key={l.id} className="px-6 py-4 flex items-start justify-between gap-6">
                <div>
                  <p className="font-display text-xl">{l.name} <span className="text-muted-foreground text-base">· {l.company}</span></p>
                  <p className="font-mono-data text-xs text-muted-foreground mt-1">
                    <Mail className="w-3 h-3 inline mr-1" /> {l.email}
                    {l.phone ? ` · ${l.phone}` : ""}
                    {l.role ? ` · ${l.role}` : ""}
                  </p>
                  {l.message && <p className="mt-2 text-sm text-muted-foreground italic">"{l.message}"</p>}
                </div>
                <span className="font-mono-data text-xs text-muted-foreground whitespace-nowrap">
                  {fmtDate(l.created_at)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
